import { z } from 'zod';

import { SharedLib } from '@shared';

/**
 * Состояние экрана чтения документа: **какой день** и **какая страница текста**.
 *
 * Обе величины живут в адресе, и обе по одной причине: «п. 5.7 в редакции, действовавшей на
 * 01.03.2026» — это ссылка, которую юрист вставляет в заключение, а не описание четырёх кликов.
 * Дата, оставленная в состоянии компонента, открывалась бы у получателя ссылки как сегодняшняя
 * редакция — то есть как **другой** документ (`rules/legal-documents.mdc` §15).
 */

/**
 * Дата, на которую читают, — каноническое имя `asOf`.
 *
 * Необязательна, и отсутствие значит «сегодня»: сервер подставляет текущий день и **возвращает его
 * в ответе**. Именно ответ, а не адрес, и показывается на экране постоянным индикатором — иначе
 * фраза «на эту дату документ не действовал» непроверяема, потому что читатель не знает, о каком
 * дне она.
 */
const asOfDay = z.iso.date().optional().catch(undefined);

/**
 * Узлов на странице. Меньше, чем строк в таблице, и это свойство материала: узел документа — абзац,
 * а не строка, и сотня абзацев на экране — это выгрузка, а не просмотр. Совпадает с умолчанием
 * сервера, чтобы первая страница по ссылке без параметров была той же, что открыл автор ссылки.
 */
export const PREVIEW_CLAUSES_PER_PAGE = 20;

/**
 * Пункт, к которому перешли, — часть адреса, а не состояние компонента.
 *
 * Переход по оглавлению и переход из ответа ассистента обязаны быть **воспроизводимой ссылкой**: без
 * этого «открой п. 5.7» остаётся описанием кликов. Страницу по пункту считает сервер, поэтому
 * `page` при переходе не нужен.
 *
 * `catch` обязателен: мусор в параметре не должен ронять маршрут — пункт с таким ключом просто не
 * найдётся, и экран скажет об этом. Whitelist невозможен и не нужен: `clauseId` собирается из вида и
 * нормализованного номера, и его вторая, клиентская, копия расходилась бы с серверной.
 */
const clauseAnchor = z.string().min(1).max(512).optional().catch(undefined);

export const documentPreviewSearchSchema = z.object({
  asOf: asOfDay,
  clause: clauseAnchor,
  page: z.coerce
    .number()
    .int()
    .min(1)
    .catch(SharedLib.DEFAULT_PAGE)
    .default(SharedLib.DEFAULT_PAGE),
});

export type DocumentPreviewSearch = z.infer<typeof documentPreviewSearchSchema>;
